import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { receivingApi } from "../../shared/receivingApi";
import InputNumberOnly from "../../components/controls/InputNumberOnly";
import DollarInputText from "../../components/controls/DollarInputText";
import DatePickerInput from "../../components/controls/DatePickerInput";
import SelectMenu from "../../components/controls/SelectMenu";
import InputTextOnly from "../../components/controls/InputTextOnly";
import TextArea from "../../components/controls/TextArea";
import EmptyModal from "../../components/modals/EmptyModal";
import type { SelectOption } from "../../types/api";
import type {
  ReceivingInvoice,
  ReceivingInvoiceItem,
  ReceivingProductOption,
  ReceivingStatus,
  SupplierOption,
  WarehouseLocation,
} from "../../types/receiving";
import type { WarehouseLocationOption } from "../../types/inventory";

interface ReceivingItemForm {
  id: number | null;
  product_id: number | "";
  warehouse_id: number | "";
  warehouse_location_id: number | "";
  expiry_date: string;
  quantity: string;
  unit_cost: string;
  total_cost: string;
  track_shelf_life: boolean;
}

interface InvoiceFields {
  supplier_id: number | "";
  invoice_number: string;
  invoice_date: string;
  notes: string;
}

interface WarehouseChoice extends SelectOption<number> {
  code: string;
  locations: WarehouseLocation[];
}

type ProductChoice = Pick<ReceivingProductOption, "value" | "label"> & {
  track_shelf_life?: boolean | number | string;
};

const emptyItem: ReceivingItemForm = {
  id: null,
  product_id: "",
  warehouse_id: "",
  warehouse_location_id: "",
  expiry_date: "",
  quantity: "",
  unit_cost: "",
  total_cost: "",
  track_shelf_life: false,
};
const productTracksShelfLife = (product?: { track_shelf_life?: boolean | number | string } | null) =>
  Boolean(
    product?.track_shelf_life === true ||
    product?.track_shelf_life === 1 ||
    product?.track_shelf_life === "1"
  );
const quantityText = (value: string | number | null | undefined) =>
  Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 4 });
const moneyText = (value: string | number | null | undefined) =>
  Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });

const statusLabels: Record<ReceivingStatus, string> = { open: "Open", closed: "Closed", void: "Void" };
const statusOptions: Array<SelectOption<ReceivingStatus>> = Object.entries(statusLabels).map(
  ([value, label]) => ({ value: value as ReceivingStatus, label }),
);

export default function ReceivingInvoiceDetails() {
  const { invoiceId = "" } = useParams<{ invoiceId: string }>();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState<ReceivingInvoice | null>(null);
  const [warehouses, setWarehouses] = useState<WarehouseChoice[]>([]);
  const [locations, setLocations] = useState<WarehouseLocationOption[]>([]);
  const [products, setProducts] = useState<ProductChoice[]>([]);
  const [suppliers, setSuppliers] = useState<SupplierOption[]>([]);
  const [invoiceFields, setInvoiceFields] = useState<InvoiceFields>({
    supplier_id: "",
    invoice_number: "",
    invoice_date: "",
    notes: "",
  });
  const [selectedStatus, setSelectedStatus] = useState<ReceivingStatus>("open");
  const [item, setItem] = useState(emptyItem);
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const result = await receivingApi.invoice(invoiceId);
    if (result.success) {
      setInvoice(result.data);
      setInvoiceFields({
        supplier_id: result.data.supplier_id,
        invoice_number: result.data.invoice_number || "",
        invoice_date: result.data.invoice_date || "",
        notes: result.data.notes || "",
      });
      setSelectedStatus(result.data.status);
    } else setError(result.data?.message || "Could not load receiving invoice.");
  };

  useEffect(() => {
    load();
    receivingApi.warehouses().then((r) => {
      if (!r.success) return;
      setWarehouses(
        (r.data || []).map((warehouse) => ({
          value: warehouse.id,
          label: warehouse.name,
          code: warehouse.code,
          locations: warehouse.locations || [],
        }))
      );
    });
    receivingApi.products("").then((r) => r.success && setProducts(r.data || []));
    receivingApi.suppliers().then((r) => r.success && setSuppliers(r.data || []));
  }, [invoiceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedOption = <T extends SelectOption<number>>(options: T[], value: number | "") =>
    options.find((option) => Number(option.value) === Number(value)) || null;
  const locationText = (line: ReceivingInvoiceItem) => {
    const warehouse = warehouses.find(
      (option) => Number(option.value) === Number(line.warehouse_id)
    );
    const location = warehouse?.locations?.find(
      (option) => Number(option.id) === Number(line.warehouse_location_id)
    );
    const warehouseLabel = warehouse?.label;
    const locationLabel = location?.name;
    if (warehouseLabel && locationLabel) return `${warehouseLabel} · ${locationLabel}`;
    return locationLabel || warehouseLabel || "—";
  };
  const updateSelect = (
    name: "warehouse_location_id",
    event: { target: { value: WarehouseLocationOption | null } },
  ) =>
    setItem((prev) => ({ ...prev, [name]: event.target.value?.value ?? "" }));
  const updateDate = (event: { target: { value: Date | null } }) => {
    const date = event.target.value;
    const value = date
      ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
      : "";
    setItem((prev) => ({ ...prev, expiry_date: value }));
  };

  const syncCostsFromQuantity = (quantity: string, unitCost: string, totalCost: string) => {
    const qty = Number(quantity);
    if (!qty || Number.isNaN(qty)) return { unit_cost: unitCost, total_cost: totalCost };
    if (unitCost !== "" && !Number.isNaN(Number(unitCost))) {
      return {
        unit_cost: unitCost,
        total_cost: String(Number((Number(unitCost) * qty).toFixed(4))),
      };
    }
    if (totalCost !== "" && !Number.isNaN(Number(totalCost))) {
      return {
        unit_cost: String(Number((Number(totalCost) / qty).toFixed(6))),
        total_cost: totalCost,
      };
    }
    return { unit_cost: unitCost, total_cost: totalCost };
  };

  const updateQuantity = (value: string | number) => {
    const nextValue = String(value);
    setItem((prev) => {
      const costs = syncCostsFromQuantity(nextValue, prev.unit_cost, prev.total_cost);
      return { ...prev, quantity: nextValue, ...costs };
    });
  };

  const updateUnitCost = (value: string | number) => {
    const nextValue = String(value);
    setItem((prev) => {
      const qty = Number(prev.quantity);
      const total =
        nextValue !== "" && qty && !Number.isNaN(Number(nextValue))
          ? String(Number((Number(nextValue) * qty).toFixed(4)))
          : prev.total_cost;
      return { ...prev, unit_cost: nextValue, total_cost: total };
    });
  };

  const updateTotalCost = (value: string | number) => {
    const nextValue = String(value);
    setItem((prev) => {
      const qty = Number(prev.quantity);
      const unit =
        nextValue !== "" && qty && !Number.isNaN(Number(nextValue))
          ? String(Number((Number(nextValue) / qty).toFixed(6)))
          : prev.unit_cost;
      return { ...prev, total_cost: nextValue, unit_cost: unit };
    });
  };

  const selectWarehouse = async (value: number | "") => {
    setItem((prev) => ({ ...prev, warehouse_id: value, warehouse_location_id: "" }));
    if (!value) return setLocations([]);
    const result = await receivingApi.locations(value);
    if (result.success) setLocations(result.data);
  };

  const closeItemModal = () => {
    setItemModalOpen(false);
    setItem(emptyItem);
    setLocations([]);
  };

  const openAddItem = () => {
    setItem(emptyItem);
    setLocations([]);
    setItemModalOpen(true);
  };

  const editItem = async (existing: ReceivingInvoiceItem) => {
    const result = await receivingApi.locations(existing.warehouse_id);
    if (result.success) setLocations(result.data);
    setProducts((prev) => {
      const exists = prev.some((p) => Number(p.value) === Number(existing.product_id));
      if (exists) return prev;
      return [
        ...prev,
        {
          value: existing.product_id,
          label: `${existing.product_name}${existing.sku ? ` - ${existing.sku}` : ""}`,
          track_shelf_life: existing.product?.track_shelf_life ?? false,
        },
      ];
    });
    const quantity = existing.quantity;
    const unitCost = existing.unit_cost;
    const totalCost =
      quantity != null && unitCost != null
        ? String(Number((Number(unitCost) * Number(quantity)).toFixed(4)))
        : (existing.total_cost ?? "");
    setItem({
      id: existing.id,
      product_id: existing.product_id,
      warehouse_id: existing.warehouse_id,
      warehouse_location_id: existing.warehouse_location_id,
      expiry_date: existing.expiry_date ?? "",
      quantity,
      unit_cost: unitCost,
      total_cost: totalCost,
      track_shelf_life: existing.product?.track_shelf_life ?? false,
    });
    setItemModalOpen(true);
  };

  const saveItem = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    const payload = {
      product_id: Number(item.product_id),
      warehouse_id: Number(item.warehouse_id),
      warehouse_location_id: Number(item.warehouse_location_id),
      expiry_date: productTracksShelfLife(
        products.find((p) => Number(p.value) === Number(item.product_id)) || item
      )
        ? item.expiry_date || null
        : null,
      quantity: Number(item.quantity),
      cost_input: { type: "unit" as const, value: Number(item.unit_cost) },
    };
    const result = item.id
      ? await receivingApi.updateItem(invoiceId, item.id, payload)
      : await receivingApi.addItem(invoiceId, payload);
    if (result.success) {
      closeItemModal();
      await load();
    } else {
      setError(
        result.data?.message ||
          Object.values(result.data?.errors || {}).flat()[0] ||
          "Could not save item."
      );
    }
    setSaving(false);
  };

  const remove = async (id: number) => {
    if (
      !window.confirm(
        "Remove this received line? Completed invoices will receive a reversing inventory adjustment."
      )
    )
      return;
    const result = await receivingApi.deleteItem(invoiceId, id);
    if (result.success) load();
    else setError(result.data?.message || "Could not remove item.");
  };

  const saveInvoice = async () => {
    if (!invoice) return;
    if (
      selectedStatus === "closed" &&
      invoice.status !== "closed" &&
      !window.confirm("Close this invoice and add its quantities to inventory?")
    )
      return;
    if (
      selectedStatus === "void" &&
      invoice.status !== "void" &&
      !window.confirm("Void this invoice? Posted inventory will be reversed.")
    )
      return;
    if (
      selectedStatus === "open" &&
      invoice.status === "closed" &&
      !window.confirm("Reopen this invoice and reverse posted inventory?")
    )
      return;
    if (
      selectedStatus === "open" &&
      invoice.status === "void" &&
      !window.confirm("Reopen this voided invoice?")
    )
      return;
    setSaving(true);
    setError("");
    const updateResult = await receivingApi.updateInvoice(invoiceId, {
      supplier_id: Number(invoiceFields.supplier_id),
      invoice_number: invoiceFields.invoice_number,
      invoice_date: invoiceFields.invoice_date,
      notes: invoiceFields.notes || null,
    });
    if (!updateResult.success) {
      setError(
        updateResult.data?.message ||
          Object.values(updateResult.data?.errors || {}).flat()[0] ||
          "Could not save invoice information."
      );
      setSaving(false);
      return;
    }
    if (selectedStatus !== invoice.status) {
      const statusResult = await receivingApi.updateStatus(invoiceId, selectedStatus);
      if (!statusResult.success) {
        setError(
          statusResult.data?.message ||
            Object.values(statusResult.data?.errors || {}).flat()[0] ||
            "Invoice information saved, but its status could not be updated."
        );
        setSaving(false);
        await load();
        return;
      }
    }
    await load();
    setSaving(false);
  };

  if (!invoice) return <div className="p-6 text-gray-500">Loading receiving invoice…</div>;

  const tracksShelfLife = productTracksShelfLife(
    products.find((p) => Number(p.value) === Number(item.product_id)) || item
  );

  const itemForm = (
    <form onSubmit={saveItem}>
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            {item.id ? "Correct received item" : "Add received item"}
          </h2>
          <p className="text-sm text-gray-500">
            Pick a product, then enter unit cost or total line cost — the other updates from
            quantity.
          </p>
        </div>
        <button
          type="button"
          onClick={closeItemModal}
          className="text-sm font-semibold text-gray-500 hover:text-gray-700"
        >
          Close
        </button>
      </div>
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <SelectMenu
          parentClass="md:col-span-2"
          title="Product"
          name="product_id"
          options={products}
          value={selectedOption(products, item.product_id)}
          handleInputChange={(e) => {
            const value = e.target.value?.value ?? "";
            const nextProduct = products.find((p) => Number(p.value) === Number(value));
            setItem((prev) => ({
              ...prev,
              product_id: value,
              track_shelf_life: productTracksShelfLife(nextProduct),
              expiry_date: productTracksShelfLife(nextProduct) ? prev.expiry_date : "",
            }));
          }}
          placeholder="Search or select a product..."
          isClearable={true}
        />
        <SelectMenu
          title="Warehouse"
          name="warehouse_id"
          options={warehouses.map((w) => ({ ...w, label: `${w.label} (${w.code})` }))}
          value={selectedOption(warehouses, item.warehouse_id)}
          handleInputChange={(e) => selectWarehouse(e.target.value?.value ?? "")}
          placeholder="Select warehouse"
        />
        <SelectMenu
          title="Location"
          name="warehouse_location_id"
          options={locations.map((l) => ({ ...l, label: `${l.label} (${l.code})` }))}
          value={selectedOption(locations, item.warehouse_location_id)}
          handleInputChange={(e) => updateSelect("warehouse_location_id", e)}
          placeholder="Select location"
        />
        {tracksShelfLife ? (
          <DatePickerInput
            label="Expiry date"
            name="expiry_date"
            labelOn={true}
            value={item.expiry_date}
            onChange={updateDate}
          />
        ) : null}
        <InputNumberOnly
          label="Quantity"
          name="quantity"
          labelOn={true}
          value={item.quantity === "" ? null : Number(item.quantity)}
          onChange={(e) => updateQuantity(e.target.value)}
          className="w-full"
          min={0.0001}
          minFractionDigits={0}
          maxFractionDigits={4}
        />
        <DollarInputText
          label="Unit cost"
          name="unit_cost"
          labelOn={true}
          value={item.unit_cost === "" ? null : Number(item.unit_cost)}
          onChange={(e) => updateUnitCost(e.target.value)}
          min={0}
          minFractionDigits={2}
          maxFractionDigits={4}
        />
        <DollarInputText
          label="Total line cost"
          name="total_cost"
          labelOn={true}
          value={item.total_cost === "" ? null : Number(item.total_cost)}
          onChange={(e) => updateTotalCost(e.target.value)}
          min={0}
          minFractionDigits={2}
          maxFractionDigits={4}
        />
      </div>
      <div className="mt-5 flex justify-end">
        <button
          disabled={saving}
          className="rounded-lg bg-primary-600 px-4 py-2 font-semibold text-white disabled:opacity-50"
        >
          {saving ? "Saving…" : item.id ? "Save correction" : "Add item"}
        </button>
      </div>
    </form>
  );

  return (
    <div className="p-6 space-y-5">
      <section className="rounded-xl border border-gray-200 bg-white p-5">
        <button
          onClick={() => navigate("/receiving")}
          className="text-sm font-semibold text-primary-600"
        >
          ← Receiving invoices
        </button>
        <div className="mt-4 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-primary-600">
              {statusLabels[invoice.status] || invoice.status}
            </p>
            <h1 className="text-2xl font-bold text-gray-900">
              {invoice.supplier?.name} · {invoice.invoice_number}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              {invoice.invoice_date} · revision {invoice.version}
            </p>
          </div>
          <div className="flex items-end gap-5">
            <div className="text-right">
              <p className="text-xs uppercase tracking-wide text-gray-400">Received value</p>
              <p className="font-mono text-2xl font-bold tabular-nums text-gray-900">
                ${invoice.total_cost}
              </p>
            </div>
            <SelectMenu
              parentClass="w-52 text-left"
              title="Status"
              name="status"
              options={statusOptions}
              value={statusOptions.find((option) => option.value === selectedStatus)}
              handleInputChange={(event) =>
                setSelectedStatus(event.target.value?.value ?? selectedStatus)
              }
            />
            <button
              type="button"
              onClick={saveInvoice}
              disabled={saving}
              className="rounded-lg bg-indigo-600 px-4 py-2 font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </section>

      {error && (
        <p className="rounded-lg bg-red-50 p-3 text-sm font-medium text-red-700">{error}</p>
      )}

      <section className="rounded-xl border border-gray-200 bg-white p-5">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Invoice information</h2>
          <p className="text-sm text-gray-500">Update the invoice, then select Save.</p>
        </div>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <SelectMenu
            title="Supplier"
            name="supplier_id"
            options={suppliers}
            value={
              suppliers.find(
                (option) => Number(option.value) === Number(invoiceFields.supplier_id)
              ) || null
            }
            handleInputChange={(event) =>
              setInvoiceFields({ ...invoiceFields, supplier_id: event.target.value?.value ?? "" })
            }
          />
          <InputTextOnly
            label="Supplier invoice number"
            name="invoice_number"
            labelOn={true}
            value={invoiceFields.invoice_number}
            onChange={(event) =>
              setInvoiceFields({ ...invoiceFields, invoice_number: event.target.value })
            }
            className="w-full"
          />
          <DatePickerInput
            label="Invoice date"
            name="invoice_date"
            labelOn={true}
            value={invoiceFields.invoice_date}
            onChange={(event) => {
              const date = event.target.value;
              setInvoiceFields({
                ...invoiceFields,
                invoice_date: date
                  ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
                  : "",
              });
            }}
          />
          <TextArea
            label="Notes"
            name="notes"
            labelOn={true}
            value={invoiceFields.notes}
            onChange={(event) => setInvoiceFields({ ...invoiceFields, notes: event.target.value })}
            rows={3}
          />
        </div>
      </section>

      <section className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        <div className="flex items-center justify-between gap-3 border-b border-gray-200 p-5">
          <div>
            <h2 className="font-semibold text-gray-900">Received lines</h2>
            <p className="text-sm text-gray-500">
              {invoice.status === "void"
                ? "Void invoice lines are locked because their inventory effect has already been reversed."
                : "Add items here, or edit a line to correct quantity, location, or cost."}
            </p>
          </div>
          {invoice.status !== "void" && (
            <button
              type="button"
              onClick={openAddItem}
              className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700"
            >
              Add item
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Product
                </th>
                <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Location
                </th>
                <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Expiry
                </th>
                <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Quantity
                </th>
                <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Unit cost
                </th>
                <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Line total
                </th>
                {invoice.status !== "void" && (
                  <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {invoice.items.length === 0 ? (
                <tr>
                  <td
                    colSpan={invoice.status !== "void" ? 7 : 6}
                    className="px-5 py-8 text-center text-gray-500"
                  >
                    No products received yet.
                  </td>
                </tr>
              ) : (
                invoice.items.map((line) => (
                  <tr key={line.id}>
                    <td className="px-5 py-4">
                      <p className="font-semibold text-gray-900">
                        {line.product_name || "Product"}
                      </p>
                      <p className="text-xs text-gray-500">SKU {line.sku || line.barcode || "—"}</p>
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-700">{locationText(line)}</td>
                    <td className="px-5 py-4 font-mono text-sm tabular-nums text-gray-700">
                      {line.expiry_date || "—"}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <p className="font-mono tabular-nums">{quantityText(line.quantity)}</p>
                      <p className="text-xs text-gray-500">
                        {line.unit_of_measure?.label || "received"}
                      </p>
                    </td>
                    <td className="px-5 py-4 text-right font-mono tabular-nums">
                      ${moneyText(line.unit_cost)}
                    </td>
                    <td className="px-5 py-4 text-right font-mono tabular-nums">
                      ${moneyText(line.total_cost)}
                    </td>
                    {invoice.status !== "void" && (
                      <td className="px-5 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => editItem(line)}
                            className="rounded-md border px-3 py-1 text-sm font-semibold"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => remove(line.id)}
                            className="rounded-md border border-red-200 px-3 py-1 text-sm font-semibold text-red-600"
                          >
                            Remove
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <EmptyModal
        open={itemModalOpen}
        setOpen={(next) => {
          if (!next?.open) closeItemModal();
        }}
        childComponent={itemModalOpen ? itemForm : null}
      />
    </div>
  );
}
